import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FormField } from '../shared';
import {
  CLOUD_OPTIONS,
  AWS_GPU_REGIONS,
  GCP_GPU_REGIONS,
  COREWEAVE_REGIONS,
  ALLOCATION_STRATEGIES,
  AWS_MACHINE_TYPES,
  GCP_MACHINE_TYPES,
  COREWEAVE_GPU_TYPES,
} from '../../constants/regions';
import type { CloudProvider, AllocationStrategy } from '../../constants/regions';
import type { InfrastructureFormData, BackendFormData } from '../../types/profile.types';
import { id3 } from '@interlucent/ids';
import styles from './InfrastructureEditor.module.css';

/** Shape of the POOL_INFRA_DEFAULTS object from the gateway */
interface AwsRegionDefaults {
  securityGroupId: string;
  subnetIds: string[];
  amiId: string;
}

interface PoolInfraDefaults {
  aws?: {
    instanceType: string;
    regions: Record<string, AwsRegionDefaults>;
  };
  gcp?: {
    machineType: string;
    firewallTag: string;
    imageId: string;
    project: string;
    network: string;
    serviceAccount?: string;
  };
  coreweave?: {
    // Injected server-side from the POOL_CW_KUBE_TOKEN secret (never in the client
    // bundle) — same pattern as gcp.serviceAccount. Pre-fills the CW SA token field.
    kubeToken?: string;
    // Injected server-side from the POOL_CW_VS_PASSWORD secret — the orc-pool-worker
    // VirtualServer autologon password. Pre-fills the VS Password field.
    vsPassword?: string;
  };
}

interface InfrastructureEditorProps {
  value: InfrastructureFormData;
  onChange: (value: InfrastructureFormData) => void;
  disabled?: boolean;
  infraDefaults?: unknown;
}

function asDefaults(v: unknown): PoolInfraDefaults | null {
  if (v && typeof v === 'object') return v as PoolInfraDefaults;
  return null;
}

/** Generate a descriptive backend ID like "aws-useast1-a3f" */
function makeBackendId(cloud: CloudProvider, region: string): string {
  return `${cloud}-${region.replace(/[-_]/g, '')}-${id3()}`;
}

// Default OTLP endpoint pre-filled on newly-created backends. Points at the
// dev SigNoz instance provisioned by infra/observability. Operators can
// clear/override per backend; existing pools are not retroactively defaulted.
// Uses first-level subdomain (otel-dev, not otel.dev) because Cloudflare
// Universal SSL doesn't cover second-level wildcards on the zone.
// TODO(prod): route this through PoolInfraDefaults so stack-scoped envs
// pick the right backend without source edits.
const DEFAULT_OTEL_ENDPOINT = 'https://otel-dev.interlucent.ai';

// OTLP bearer token pre-filled on new backends (dev collector). Placeholder value.
const DEFAULT_OTEL_AUTH = 'simple';

// Job-board transport for newly-created backends: WS (hibernatable) — the
// cost-saving path (the JobBoard/Mux DOs hibernate at idle instead of holding an
// HTTP long-poll awake). NOTE: requires orc >= 0.10.107; older builds reject the
// `--board-transport` flag and crash on boot. Existing backends keep their value.
const DEFAULT_ORCHESTRATOR_ARGS = '--board-transport ws';

// CoreWeave dev backend defaults (non-secret — safe to keep in the bundle).
const DEFAULT_CW_API_SERVER = 'https://k8s.ord1.coreweave.com';
const DEFAULT_CW_NAMESPACE = 'tenant-kv-rd';
const DEFAULT_CW_ROOT_PVC_LABEL = 'interlucent/version=v0.1.3-legacy-up, interlucent/region=ord1';
const DEFAULT_CW_VS_USERNAME = 'orc-pool-worker';
// NOTE: the CW SA (kube) token is NOT hardcoded here — it is injected server-side from
// the POOL_CW_KUBE_TOKEN secret via /api/admin/pool-infra-defaults (defaults.coreweave.
// kubeToken), so it never lands in the client bundle. See gcp.serviceAccount for the
// same pattern.

// Newly-added backends default to launch protocol v4 (dynamic): workers fetch
// launch config from the gateway on every watchdog tick, so pool edits apply
// without re-baking instances. Existing backends keep their saved value —
// this only applies at creation (and on cloud switch, which rebuilds the
// backend from defaults anyway). Confirmed 2026-07-21.
const DEFAULT_LAUNCH_PROTOCOL_VERSION = 4 as const;

function makeBackend(cloud: CloudProvider, defaults?: PoolInfraDefaults | null): BackendFormData {
  if (cloud === 'gcp' && defaults?.gcp) {
    const gcp = defaults.gcp;
    const region = 'us-west1';
    return {
      id: makeBackendId('gcp', region),
      cloud: 'gcp',
      region,
      instanceType: gcp.machineType,
      imageId: gcp.imageId,
      weight: 1,
      gcpProjectId: gcp.project,
      gcpFirewallTags: [gcp.firewallTag, 'orc-pool-worker'].filter(Boolean).join(', '),
      gcpServiceAccount: gcp.serviceAccount,
      otelEndpoint: DEFAULT_OTEL_ENDPOINT,
      otelAuth: DEFAULT_OTEL_AUTH,
      orchestratorArgs: DEFAULT_ORCHESTRATOR_ARGS,
      launchProtocolVersion: DEFAULT_LAUNCH_PROTOCOL_VERSION,
    };
  }

  if (cloud === 'aws' && defaults?.aws) {
    const aws = defaults.aws;
    const [firstRegion, regionData] = Object.entries(aws.regions)[0] ?? [];
    const region = firstRegion ?? 'us-east-1';
    return {
      id: makeBackendId('aws', region),
      cloud: 'aws',
      region,
      instanceType: aws.instanceType,
      imageId: regionData?.amiId ?? '',
      weight: 1,
      awsSubnets: regionData?.subnetIds?.join(', ') ?? '',
      awsSecurityGroups: regionData?.securityGroupId ?? '',
      otelEndpoint: DEFAULT_OTEL_ENDPOINT,
      otelAuth: DEFAULT_OTEL_AUTH,
      orchestratorArgs: DEFAULT_ORCHESTRATOR_ARGS,
      launchProtocolVersion: DEFAULT_LAUNCH_PROTOCOL_VERSION,
    };
  }

  if (cloud === 'coreweave') {
    const region = 'ORD1';
    return {
      id: makeBackendId('coreweave', region),
      cloud: 'coreweave' as CloudProvider,
      region,
      instanceType: 'RTX_A6000',
      imageId: '',
      weight: 1,
      cwApiServer: DEFAULT_CW_API_SERVER,
      cwNamespace: DEFAULT_CW_NAMESPACE,
      cwKubeToken: defaults?.coreweave?.kubeToken,
      cwVsUsername: DEFAULT_CW_VS_USERNAME,
      cwVsPassword: defaults?.coreweave?.vsPassword,
      cwGpuCount: 1,
      cwCpuCount: 10,
      cwMemoryGb: 32,
      cwRootPvcLabel: DEFAULT_CW_ROOT_PVC_LABEL,
      cwRootDiskSize: '256Gi',
      cwAdditionalDiskSize: '500Gi',
      otelEndpoint: DEFAULT_OTEL_ENDPOINT,
      otelAuth: DEFAULT_OTEL_AUTH,
      orchestratorArgs: DEFAULT_ORCHESTRATOR_ARGS,
      launchProtocolVersion: DEFAULT_LAUNCH_PROTOCOL_VERSION,
    };
  }

  // Fallback: no defaults available
  const region = cloud === 'gcp' ? 'us-west1' : 'us-east-1';
  return {
    id: makeBackendId(cloud, region),
    cloud,
    region,
    instanceType: '',
    imageId: '',
    weight: 1,
    otelEndpoint: DEFAULT_OTEL_ENDPOINT,
    otelAuth: DEFAULT_OTEL_AUTH,
    orchestratorArgs: DEFAULT_ORCHESTRATOR_ARGS,
    launchProtocolVersion: DEFAULT_LAUNCH_PROTOCOL_VERSION,
  };
}

/** Parse a kubeconfig YAML (simple line-based parser — no YAML lib needed) */
function parseKubeconfig(text: string): { server?: string; token?: string; namespace?: string } | null {
  try {
    // Extract server URL
    const serverMatch = text.match(/server:\s*(\S+)/);
    // Extract token (from users[].user.token)
    const tokenMatch = text.match(/token:\s*(\S+)/);
    // Extract namespace (from contexts[].context.namespace)
    const nsMatch = text.match(/namespace:\s*(\S+)/);

    if (!serverMatch && !tokenMatch) return null;

    return {
      server: serverMatch?.[1],
      token: tokenMatch?.[1],
      namespace: nsMatch?.[1],
    };
  } catch {
    return null;
  }
}

function getRegionsForCloud(cloud: CloudProvider) {
  if (cloud === 'gcp') return GCP_GPU_REGIONS;
  if (cloud === 'coreweave') return COREWEAVE_REGIONS;
  return AWS_GPU_REGIONS;
}

export function InfrastructureEditor({ value, onChange, disabled, infraDefaults }: InfrastructureEditorProps) {
  const [expandedAdvanced, setExpandedAdvanced] = useState<Set<string>>(new Set());
  const defaults = asDefaults(infraDefaults);

  // Backfill defaults into existing backends when infraDefaults loads or changes
  const prevDefaultsRef = useRef(defaults);
  useEffect(() => {
    if (!defaults || defaults === prevDefaultsRef.current) {
      prevDefaultsRef.current = defaults;
      return;
    }
    prevDefaultsRef.current = defaults;

    let changed = false;
    const next = value.backends.map(b => {
      if (b.cloud === 'gcp' && defaults.gcp) {
        const gcp = defaults.gcp;
        const patched = { ...b };
        if (!patched.instanceType) { patched.instanceType = gcp.machineType; changed = true; }
        if (!patched.imageId) { patched.imageId = gcp.imageId; changed = true; }
        if (!patched.gcpProjectId) { patched.gcpProjectId = gcp.project; changed = true; }
        if (!patched.gcpFirewallTags) { patched.gcpFirewallTags = [gcp.firewallTag, 'orc-pool-worker'].filter(Boolean).join(', '); changed = true; }
        if (!patched.gcpServiceAccount && gcp.serviceAccount) { patched.gcpServiceAccount = gcp.serviceAccount; changed = true; }
        return patched;
      }
      if (b.cloud === 'aws' && defaults.aws) {
        const aws = defaults.aws;
        const regionData = aws.regions[b.region];
        const patched = { ...b };
        if (!patched.instanceType) { patched.instanceType = aws.instanceType; changed = true; }
        if (regionData) {
          if (!patched.imageId) { patched.imageId = regionData.amiId; changed = true; }
          if (!patched.awsSubnets) { patched.awsSubnets = regionData.subnetIds.join(', '); changed = true; }
          if (!patched.awsSecurityGroups) { patched.awsSecurityGroups = regionData.securityGroupId; changed = true; }
        }
        return patched;
      }
      return b;
    });
    if (changed) {
      onChange({ ...value, backends: next });
    }
  }, [defaults, value, onChange]);

  const updateBackend = useCallback((index: number, updates: Partial<BackendFormData>) => {
    const next = [...value.backends];
    next[index] = { ...next[index], ...updates };
    // When cloud changes, re-populate with defaults for the new cloud
    if (updates.cloud) {
      const defaultBackend = makeBackend(updates.cloud, defaults);
      next[index] = { ...defaultBackend, weight: next[index].weight };
    }
    // When AWS region changes, update region-specific fields from defaults
    if (updates.region && next[index].cloud === 'aws' && defaults?.aws) {
      const regionData = defaults.aws.regions[updates.region];
      if (regionData) {
        next[index].imageId = regionData.amiId;
        next[index].awsSubnets = regionData.subnetIds.join(', ');
        next[index].awsSecurityGroups = regionData.securityGroupId;
      }
    }
    // Keep ID in sync with cloud + region
    if (updates.cloud || updates.region) {
      next[index].id = makeBackendId(next[index].cloud, next[index].region);
    }
    onChange({ ...value, backends: next });
  }, [value, onChange, defaults]);

  const removeBackend = useCallback((index: number) => {
    const next = value.backends.filter((_, i) => i !== index);
    onChange({ ...value, backends: next });
  }, [value, onChange]);

  const addBackend = useCallback((cloud: CloudProvider) => {
    onChange({ ...value, backends: [...value.backends, makeBackend(cloud, defaults)] });
  }, [value, onChange, defaults]);

  const toggleAdvanced = useCallback((id: string) => {
    setExpandedAdvanced(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <div className={styles.container}>
      <FormField
        label="Allocation Strategy"
        htmlFor="allocation-strategy"
        helperText="How to distribute sessions across backends"
      >
        <select
          id="allocation-strategy"
          value={value.allocationStrategy}
          onChange={(e) => onChange({ ...value, allocationStrategy: e.target.value as AllocationStrategy })}
          disabled={disabled}
        >
          {ALLOCATION_STRATEGIES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </FormField>

      <p className={styles.sectionLabel}>Backends</p>

      {value.backends.map((backend, index) => {
        const regions = getRegionsForCloud(backend.cloud);
        const showAdvanced = expandedAdvanced.has(backend.id);

        // GCP machine types are region-gated. Each region carries the
        // families GCP actually builds there (measured, see regions.ts), so
        // g4 never appears for europe-west6 and g2 never appears for
        // australia-southeast1 — picking one there fails at template create,
        // not at scale-up, which is a much worse place to find out. AWS and
        // CoreWeave keep their full lists; their regions are uniform enough
        // that the gate would be noise.
        const gcpFamilies =
          backend.cloud === 'gcp'
            ? GCP_GPU_REGIONS.find(r => r.value === backend.region)?.families
            : undefined;
        const machineOptions: ReadonlyArray<{ value: string; label: string }> =
          backend.cloud === 'gcp'
            ? gcpFamilies
              ? GCP_MACHINE_TYPES.filter(m =>
                  (gcpFamilies as readonly string[]).includes(m.family),
                )
              : GCP_MACHINE_TYPES
            : backend.cloud === 'aws'
              ? AWS_MACHINE_TYPES
              : backend.cloud === 'coreweave'
                ? COREWEAVE_GPU_TYPES
                : [];

        // Only warn about a shape we KNOW is a GCP machine type whose family
        // the region cannot build. An unrecognized string is someone's
        // deliberate custom value (n2-*, a future family) and is left alone.
        const knownGcpShape = GCP_MACHINE_TYPES.find(m => m.value === backend.instanceType);
        const shapeUnavailableHere =
          knownGcpShape &&
          gcpFamilies &&
          !(gcpFamilies as readonly string[]).includes(knownGcpShape.family);

        // vWS vs bare is not only a licence toggle — they are separate GCP
        // accelerator types with separate regional quota, so the choice can
        // decide whether a region has any capacity for this pool at all.
        const gcpFamily =
          knownGcpShape?.family ?? backend.instanceType?.match(/^([a-z][0-9])-/)?.[1];
        const defaultVariantLabel = gcpFamily === 'g2' ? 'standard' : 'vWS';
        const gpuVariantHint =
          gcpFamily === 'g2'
            ? 'vWS adds $0.20/GPU/hr and spends the L4 vWS grant (32/region by default) instead of the bare L4 one, which is larger in our biggest regions.'
            : gcpFamily === 'g4'
              ? 'vWS adds $0.20/GPU/hr and is what gives WDDM. Grants differ per region — us-west1 holds bare RTX PRO 6000 but no vWS.'
              : gcpFamily === 'n1'
                ? 'Picks nvidia-tesla-t4-vws or the bare nvidia-tesla-t4. vWS adds $0.20/GPU/hr.'
                : 'A100 and H100 have no vWS part; this is ignored for a2/a3.';

        return (
          <div key={backend.id} className={styles.backendCard}>
            <div className={styles.backendHeader}>
              <h4 className={styles.backendTitle}>Backend {index + 1}</h4>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => removeBackend(index)}
                disabled={disabled}
              >
                Remove
              </button>
            </div>

            <div className={styles.fieldRow}>
              <FormField label="Cloud Provider" htmlFor={`cloud-${backend.id}`} required>
                <select
                  id={`cloud-${backend.id}`}
                  value={backend.cloud}
                  onChange={(e) => updateBackend(index, { cloud: e.target.value as CloudProvider })}
                  disabled={disabled}
                >
                  {CLOUD_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </FormField>

              <FormField label="Region" htmlFor={`region-${backend.id}`} required>
                <select
                  id={`region-${backend.id}`}
                  value={backend.region}
                  onChange={(e) => updateBackend(index, { region: e.target.value })}
                  disabled={disabled}
                >
                  {regions.map(r => (
                    <option key={r.value} value={r.value}>
                      {r.label} ({r.gpu})
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <div className={styles.fieldRow}>
              <FormField
                label={backend.cloud === 'coreweave' ? 'GPU Type' : 'Instance Type'}
                htmlFor={`instance-${backend.id}`}
                helperText={
                  shapeUnavailableHere
                    ? `GCP does not offer ${backend.instanceType} in ${backend.region}.`
                    : undefined
                }
                required
              >
                {(() => {
                  // Dropdown for all clouds. Each cloud has its own
                  // preset list — AWS and GCP are machine types (which
                  // bundle CPU/memory/GPU into a single SKU), CoreWeave
                  // is GPU type only (CPU/memory are separate fields
                  // below). If the backend's current value isn't in
                  // the preset list (e.g. a legacy config with a custom
                  // GPU name or machine type, or a shape this region
                  // can't build), surface it as a "(custom)" option
                  // rather than silently clobbering the value on first
                  // render.
                  const options = machineOptions;
                  const currentValue = backend.instanceType;
                  const isPreset = options.some((o) => o.value === currentValue);
                  return (
                    <select
                      id={`instance-${backend.id}`}
                      value={currentValue}
                      onChange={(e) => updateBackend(index, { instanceType: e.target.value })}
                      disabled={disabled}
                    >
                      {!isPreset && currentValue && (
                        <option value={currentValue}>{currentValue} (custom)</option>
                      )}
                      {options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  );
                })()}
              </FormField>

              {backend.cloud !== 'coreweave' && (
                <FormField label="Image ID" htmlFor={`image-${backend.id}`} required>
                  <input
                    id={`image-${backend.id}`}
                    type="text"
                    value={backend.imageId}
                    onChange={(e) => updateBackend(index, { imageId: e.target.value })}
                    placeholder={backend.cloud === 'gcp' ? 'projects/.../images/...' : 'ami-...'}
                    disabled={disabled}
                  />
                </FormField>
              )}
            </div>

            <div className={styles.fieldRow}>
              <FormField label="Weight" htmlFor={`weight-${backend.id}`} helperText="Traffic distribution weight">
                <input
                  id={`weight-${backend.id}`}
                  type="number"
                  min={1}
                  value={backend.weight}
                  onChange={(e) => updateBackend(index, { weight: parseInt(e.target.value) || 1 })}
                  disabled={disabled}
                />
              </FormField>

              <FormField label="Sessions per machine" htmlFor={`mc-${backend.id}`} helperText="Concurrent sessions per instance">
                <input
                  id={`mc-${backend.id}`}
                  type="number"
                  min={1}
                  value={backend.maxConcurrent ?? 1}
                  onChange={(e) => updateBackend(index, { maxConcurrent: parseInt(e.target.value) || 1 })}
                  disabled={disabled}
                />
              </FormField>

              <FormField label="Startup Mode" htmlFor={`sm-${backend.id}`} helperText="Standard = full UserData, Legacy-derived = IL_* env vars only (for legacy-derived images)">
                <select
                  id={`sm-${backend.id}`}
                  value={backend.startupMode ?? 'standard'}
                  onChange={(e) => updateBackend(index, { startupMode: e.target.value as 'standard' | 'legacy-derived' })}
                  disabled={disabled}
                >
                  <option value="standard">Standard</option>
                  <option value="legacy-derived">Legacy-derived</option>
                </select>
              </FormField>

              <FormField
                label="Launch Protocol"
                htmlFor={`lpv-${backend.id}`}
                helperText="v4 = dynamic (recommended for new pools): workers fetch launch config (args verbatim, sessions/machine, OTel, pinned orc version) from the gateway on every watchdog tick — pool edits apply without re-baking instances. v3 = astra-driven: launch values baked as typed IL_* env vars at instance creation; arguments are mapped onto the known flag knobs (unknown args are dropped) — older pools/orcs depend on this shape. v1 = legacy (same script bytes as v3, no IL_PROTOCOL_VERSION emit). v2 = image-baked startup.ps1 authoritative (deferred — Packer cycle too slow)."
              >
                <select
                  id={`lpv-${backend.id}`}
                  value={backend.launchProtocolVersion ?? 1}
                  onChange={(e) => updateBackend(index, { launchProtocolVersion: Number(e.target.value) as 1 | 2 | 3 | 4 })}
                  disabled={disabled}
                >
                  <option value={4}>v4 (dynamic — runtime launch config, recommended)</option>
                  <option value={3}>v3 (astra-driven — baked IL_* env vars)</option>
                  <option value={1}>v1 (legacy, silent default)</option>
                  <option value={2}>v2 (clean — image-baked startup.ps1)</option>
                </select>
              </FormField>

              <FormField label="Max Capacity" htmlFor={`maxcap-${backend.id}`} helperText="Optional instance limit">
                <input
                  id={`maxcap-${backend.id}`}
                  type="number"
                  min={0}
                  value={backend.maxCapacity ?? ''}
                  onChange={(e) => updateBackend(index, { maxCapacity: e.target.value ? parseInt(e.target.value) : undefined })}
                  placeholder="Unlimited"
                  disabled={disabled}
                />
              </FormField>
            </div>

            {/* Orchestrator launch: free-form orc CLI arguments plus
                observability wiring. Delivery depends on the launch protocol:
                v4 fetches them verbatim at runtime via the launch-config
                endpoint; v1/v3 map them onto the typed IL_* flag knobs baked
                at instance creation (unknown args are dropped); v2 uses the
                image-baked script and ignores them. */}
            <div className={styles.flagsSection}>
              <div className={styles.flagsHeader}>
                Orchestrator launch
                <span className={styles.flagsHeaderHelper}>
                  Free-form orc-orchestrator CLI arguments + observability wiring
                  {backend.launchProtocolVersion === 2 ? ' (arguments ignored under launch protocol v2)' : ''}
                </span>
              </div>

              <FormField
                label="Orchestrator arguments"
                htmlFor={`orchargs-${backend.id}`}
                helperText="Appended to the orc-orchestrator launch on every worker in this backend. Space-separated, e.g. --ultra-fast-slots --profile-pool --log-level orc_orchestrator=info. Delivered verbatim under launch protocol v4; under v1/v3 only the known flags below can travel (they map to typed IL_* env vars)."
              >
                <textarea
                  id={`orchargs-${backend.id}`}
                  className={styles.argsTextarea}
                  value={backend.orchestratorArgs ?? ''}
                  onChange={(e) => updateBackend(index, { orchestratorArgs: e.target.value })}
                  placeholder="--ultra-fast-slots --profile-pool --log-level orc_orchestrator=info,orc_clerical=info"
                  rows={3}
                  spellCheck={false}
                  disabled={disabled}
                />
              </FormField>

              <div className={styles.argsReference}>
                <span className={styles.argsReferenceTitle}>Common flags</span>
                <ul>
                  <li><code>--ultra-fast-slots</code> — single shared OS user + workspace at <code>&lt;sessions&gt;/ultra-fast/</code>, recycled at age ≥ 24h. Concurrent same-app sessions trample each other. orc ≥ 0.10.0.</li>
                  <li><code>--profile-pool</code> — Phase 3a pool users with per-session VHDX overlay; pair with <code>--ultra-fast-slots</code> for Phase 3b combined mode. orc ≥ 0.10.37.</li>
                  <li><code>--job-board false</code> — fall back to legacy WS-per-marketplace dispatch (default is the pull-based JobBoardDO).</li>
                  <li><code>--ui-isolation</code> — JOBOBJECT_BASIC_UI_RESTRICTIONS (0xA7) on each session's Job: blocks ExitWindowsEx, cross-job clipboard, USER-handle theft. orc ≥ 0.10.5.</li>
                  <li><code>--log-json-path &lt;path&gt;</code> — structured JSON log path the OTel filelog receiver picks up. Defaults to <code>D:\Logs\orc.json</code> when omitted.</li>
                  <li><code>--log-level &lt;filter&gt;</code> — tracing EnvFilter, e.g. <code>orc_orchestrator=info,orc_clerical=debug</code>.</li>
                </ul>
              </div>

              <div className={styles.flagsGrid}>
                <label className={styles.flagToggle}>
                  <input
                    type="checkbox"
                    checked={backend.streamingTarzst ?? false}
                    onChange={(e) => updateBackend(index, { streamingTarzst: e.target.checked || undefined })}
                    disabled={disabled}
                  />
                  <span>
                    <strong>MPDL_STREAMING_TARZST</strong>
                    <small>Streaming tar.zst download + 16-worker parallel extract pipeline. Network and extract overlap; wins ~20% on multi-GB tar.zst cold starts.</small>
                  </span>
                </label>
                <label className={styles.flagToggle}>
                  <input
                    type="checkbox"
                    checked={backend.acceptConfirm ?? false}
                    onChange={(e) => updateBackend(index, { acceptConfirm: e.target.checked || undefined })}
                    disabled={disabled}
                  />
                  <span>
                    <strong>ORC_ACCEPT_CONFIRM</strong>
                    <small>Accept-confirm canary (orc ≥ 0.15): direct board pulls take a tentative claim and firm it with an accept frame before serving. Existing machines pick it up on next boot after a script rotation.</small>
                  </span>
                </label>
              </div>

              <FormField
                label="OTel endpoint"
                htmlFor={`oe-${backend.id}`}
                helperText="OTLP/HTTP endpoint the on-host OTel Collector ships to. Path-less, e.g. https://otel.dev.interlucent.ai. Empty = local ndjson buffer only (no remote export)."
              >
                <input
                  id={`oe-${backend.id}`}
                  type="text"
                  value={backend.otelEndpoint ?? ''}
                  onChange={(e) => updateBackend(index, { otelEndpoint: e.target.value || undefined })}
                  placeholder="https://otel-dev.interlucent.ai"
                  disabled={disabled}
                />
              </FormField>

              <FormField
                label="OTel auth"
                htmlFor={`oa-${backend.id}`}
                helperText="Authorization header value emitted on every OTLP export. Include the scheme, e.g. 'Bearer <token>'. Empty = no auth header sent."
              >
                <input
                  id={`oa-${backend.id}`}
                  type="text"
                  value={backend.otelAuth ?? ''}
                  onChange={(e) => updateBackend(index, { otelAuth: e.target.value || undefined })}
                  placeholder="Bearer <token>"
                  disabled={disabled}
                />
              </FormField>

              <div style={{ gridColumn: '1 / -1', fontSize: '0.85em', opacity: 0.85, lineHeight: 1.5 }}>
                <strong>View SigNoz UI (dev):</strong> the dashboard is SSH-only.
                <pre style={{ margin: '0.25em 0', padding: '0.5em', background: 'rgba(0,0,0,0.06)', borderRadius: 4, overflowX: 'auto' }}>
gcloud compute ssh signoz-dev --zone=us-central1-a -- -L 8080:localhost:8080
                </pre>
                then open <code>http://localhost:8080</code> in your browser. First load shows a signup form &mdash; the first account becomes admin.
              </div>
            </div>

            <button
              type="button"
              className={styles.advancedToggle}
              onClick={() => toggleAdvanced(backend.id)}
            >
              {showAdvanced ? '- Hide advanced' : '+ Show advanced'}
            </button>

            {showAdvanced && (
              <div className={styles.advancedFields}>
                {backend.cloud === 'aws' && (
                  <>
                    <FormField label="Subnets" htmlFor={`subnets-${backend.id}`} helperText="Comma-separated subnet IDs">
                      <input
                        id={`subnets-${backend.id}`}
                        type="text"
                        value={backend.awsSubnets ?? ''}
                        onChange={(e) => updateBackend(index, { awsSubnets: e.target.value || undefined })}
                        placeholder="subnet-abc123, subnet-def456"
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField label="Security Groups" htmlFor={`sg-${backend.id}`} helperText="Comma-separated SG IDs">
                      <input
                        id={`sg-${backend.id}`}
                        type="text"
                        value={backend.awsSecurityGroups ?? ''}
                        onChange={(e) => updateBackend(index, { awsSecurityGroups: e.target.value || undefined })}
                        placeholder="sg-abc123"
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField label="Instance Profile" htmlFor={`profile-${backend.id}`}>
                      <input
                        id={`profile-${backend.id}`}
                        type="text"
                        value={backend.awsInstanceProfile ?? ''}
                        onChange={(e) => updateBackend(index, { awsInstanceProfile: e.target.value || undefined })}
                        placeholder="arn:aws:iam::..."
                        disabled={disabled}
                      />
                    </FormField>
                  </>
                )}
                {backend.cloud === 'gcp' && (
                  <>
                    <FormField label="GCP Project ID" htmlFor={`gcp-proj-${backend.id}`}>
                      <input
                        id={`gcp-proj-${backend.id}`}
                        type="text"
                        value={backend.gcpProjectId ?? ''}
                        onChange={(e) => updateBackend(index, { gcpProjectId: e.target.value || undefined })}
                        placeholder="my-project-id"
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField label="Subnetwork" htmlFor={`gcp-subnet-${backend.id}`}>
                      <input
                        id={`gcp-subnet-${backend.id}`}
                        type="text"
                        value={backend.gcpSubnetwork ?? ''}
                        onChange={(e) => updateBackend(index, { gcpSubnetwork: e.target.value || undefined })}
                        placeholder="projects/.../subnetworks/..."
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField label="Firewall Tags" htmlFor={`gcp-tags-${backend.id}`} helperText="Comma-separated network tags">
                      <input
                        id={`gcp-tags-${backend.id}`}
                        type="text"
                        value={backend.gcpFirewallTags ?? ''}
                        onChange={(e) => updateBackend(index, { gcpFirewallTags: e.target.value || undefined })}
                        placeholder="allow-health-check, allow-ssh"
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField label="Service Account" htmlFor={`gcp-sa-${backend.id}`}>
                      <input
                        id={`gcp-sa-${backend.id}`}
                        type="text"
                        value={backend.gcpServiceAccount ?? ''}
                        onChange={(e) => updateBackend(index, { gcpServiceAccount: e.target.value || undefined })}
                        placeholder="sa@project.iam.gserviceaccount.com"
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField
                      label="GPU SKU"
                      htmlFor={`gcp-gpu-sku-${backend.id}`}
                      helperText={gpuVariantHint}
                    >
                      <select
                        id={`gcp-gpu-sku-${backend.id}`}
                        value={backend.gcpGpuVariant ?? ''}
                        onChange={(e) =>
                          updateBackend(index, {
                            gcpGpuVariant: (e.target.value || undefined) as
                              | 'vws'
                              | 'standard'
                              | undefined,
                          })
                        }
                        disabled={disabled}
                      >
                        <option value="">Family default ({defaultVariantLabel})</option>
                        <option value="vws">vWS — RTX Virtual Workstation</option>
                        <option value="standard">Standard — bare compute part</option>
                      </select>
                    </FormField>
                    <FormField
                      label="Machine management"
                      htmlFor={`gcp-instance-model-${backend.id}`}
                      helperText="Direct places each machine in a zone that has capacity and skips stocked-out zones; no instance group is created. Changing this on a provisioned backend needs a reprovision."
                    >
                      <select
                        id={`gcp-instance-model-${backend.id}`}
                        value={backend.gcpInstanceModel ?? ''}
                        onChange={(e) =>
                          updateBackend(index, {
                            gcpInstanceModel: (e.target.value || undefined) as
                              | 'mig'
                              | 'direct'
                              | undefined,
                          })
                        }
                        disabled={disabled}
                      >
                        <option value="">Managed instance group (default)</option>
                        <option value="direct">Direct — pool owns each machine</option>
                      </select>
                    </FormField>
                  </>
                )}
                {backend.cloud === 'coreweave' && (
                  <>
                    <FormField label="Kubeconfig" htmlFor={`cw-kubeconfig-${backend.id}`} helperText="Upload a kubeconfig file to auto-fill server, token, and namespace">
                      <input
                        id={`cw-kubeconfig-${backend.id}`}
                        type="file"
                        accept=".yaml,.yml,.conf,.kubeconfig,*"
                        disabled={disabled}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          file.text().then((text) => {
                            const parsed = parseKubeconfig(text);
                            if (!parsed) return;
                            const updates: Partial<BackendFormData> = {};
                            if (parsed.server) updates.cwApiServer = parsed.server;
                            if (parsed.token) updates.cwKubeToken = parsed.token;
                            if (parsed.namespace) updates.cwNamespace = parsed.namespace;
                            updateBackend(index, updates);
                          });
                          // Reset so the same file can be re-selected
                          e.target.value = '';
                        }}
                      />
                    </FormField>
                    <FormField label="K8s API Server" htmlFor={`cw-api-${backend.id}`} helperText="Kubernetes API server URL">
                      <input
                        id={`cw-api-${backend.id}`}
                        type="text"
                        value={backend.cwApiServer ?? ''}
                        onChange={(e) => updateBackend(index, { cwApiServer: e.target.value || undefined })}
                        placeholder="https://k8s.ord1.coreweave.com"
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField label="K8s Bearer Token" htmlFor={`cw-token-${backend.id}`} helperText="Service account token">
                      <input
                        id={`cw-token-${backend.id}`}
                        type="password"
                        value={backend.cwKubeToken ?? ''}
                        onChange={(e) => updateBackend(index, { cwKubeToken: e.target.value || undefined })}
                        placeholder="eyJhbGci..."
                        disabled={disabled}
                      />
                    </FormField>
                    <FormField label="Namespace" htmlFor={`cw-ns-${backend.id}`}>
                      <input
                        id={`cw-ns-${backend.id}`}
                        type="text"
                        value={backend.cwNamespace ?? ''}
                        onChange={(e) => updateBackend(index, { cwNamespace: e.target.value || undefined })}
                        placeholder="lookout"
                        disabled={disabled}
                      />
                    </FormField>
                    <div className={styles.fieldRow}>
                      <FormField label="GPU Count" htmlFor={`cw-gpu-${backend.id}`} helperText="GPUs per VirtualServer">
                        <input
                          id={`cw-gpu-${backend.id}`}
                          type="number"
                          min={1}
                          value={backend.cwGpuCount ?? 1}
                          onChange={(e) => updateBackend(index, { cwGpuCount: parseInt(e.target.value) || 1 })}
                          disabled={disabled}
                        />
                      </FormField>
                      <FormField label="CPU Count" htmlFor={`cw-cpu-${backend.id}`} helperText="vCPUs per VirtualServer">
                        <input
                          id={`cw-cpu-${backend.id}`}
                          type="number"
                          min={1}
                          value={backend.cwCpuCount ?? 10}
                          onChange={(e) => updateBackend(index, { cwCpuCount: parseInt(e.target.value) || 10 })}
                          disabled={disabled}
                        />
                      </FormField>
                      <FormField label="Memory (GB)" htmlFor={`cw-mem-${backend.id}`} helperText="RAM in GB">
                        <input
                          id={`cw-mem-${backend.id}`}
                          type="number"
                          min={1}
                          value={backend.cwMemoryGb ?? 32}
                          onChange={(e) => updateBackend(index, { cwMemoryGb: parseInt(e.target.value) || 32 })}
                          disabled={disabled}
                        />
                      </FormField>
                    </div>
                    <FormField
                      label="Root PVC Label"
                      htmlFor={`cw-pvc-${backend.id}`}
                      helperText="Label selector for root disk PVC clone. Comma-separated k=v pairs matching PVC labels set by the packer build (interlucent/version, interlucent/region)."
                    >
                      <input
                        id={`cw-pvc-${backend.id}`}
                        type="text"
                        value={backend.cwRootPvcLabel ?? ''}
                        onChange={(e) => updateBackend(index, { cwRootPvcLabel: e.target.value || undefined })}
                        placeholder="interlucent/version=v0.4.0-basic, interlucent/region=ord1"
                        disabled={disabled}
                      />
                    </FormField>
                    <div className={styles.fieldRow}>
                      <FormField label="Root Disk Size" htmlFor={`cw-rootdisk-${backend.id}`} helperText="Root disk size (e.g. 256Gi)">
                        <input
                          id={`cw-rootdisk-${backend.id}`}
                          type="text"
                          value={backend.cwRootDiskSize ?? '256Gi'}
                          onChange={(e) => updateBackend(index, { cwRootDiskSize: e.target.value || undefined })}
                          placeholder="256Gi"
                          disabled={disabled}
                        />
                      </FormField>
                      <FormField label="Data Disk Size" htmlFor={`cw-datadisk-${backend.id}`} helperText="Additional volume for ReFS (e.g. 500Gi)">
                        <input
                          id={`cw-datadisk-${backend.id}`}
                          type="text"
                          value={backend.cwAdditionalDiskSize ?? '500Gi'}
                          onChange={(e) => updateBackend(index, { cwAdditionalDiskSize: e.target.value || undefined })}
                          placeholder="500Gi"
                          disabled={disabled}
                        />
                      </FormField>
                    </div>
                    <div className={styles.fieldRow}>
                      <FormField label="VS Username" htmlFor={`cw-vsuser-${backend.id}`} helperText="Autologon username (default: orc-pool-worker)">
                        <input
                          id={`cw-vsuser-${backend.id}`}
                          type="text"
                          value={backend.cwVsUsername ?? 'orc-pool-worker'}
                          onChange={(e) => updateBackend(index, { cwVsUsername: e.target.value || undefined })}
                          placeholder="orc-pool-worker"
                          disabled={disabled}
                        />
                      </FormField>
                      <FormField label="VS Password" htmlFor={`cw-vspass-${backend.id}`} helperText="Autologon password (required for CoreWeave)">
                        <input
                          id={`cw-vspass-${backend.id}`}
                          type="password"
                          value={backend.cwVsPassword ?? ''}
                          onChange={(e) => updateBackend(index, { cwVsPassword: e.target.value || undefined })}
                          placeholder="Required"
                          disabled={disabled}
                        />
                      </FormField>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className={styles.addButtons}>
        <button
          type="button"
          className={styles.addButton}
          onClick={() => addBackend('aws')}
          disabled={disabled}
        >
          + Add AWS Backend
        </button>
        <button
          type="button"
          className={styles.addButton}
          onClick={() => addBackend('gcp')}
          disabled={disabled}
        >
          + Add GCP Backend
        </button>
        <button
          type="button"
          className={styles.addButton}
          onClick={() => addBackend('coreweave')}
          disabled={disabled}
        >
          + Add CoreWeave Backend
        </button>
      </div>
    </div>
  );
}
